import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock, DocBlockType } from "@/lib/document-builder/blocks/blockTypes";
import { DOC_BLOCK_TYPES, DOC_BLOCK_TYPE_LABELS } from "@/lib/document-builder/blocks/blockTypes";
import { createDocBlock } from "@/lib/document-builder/blocks/blockDefaults";
import { DocumentBlockRow } from "./DocumentBlockRow";

type Props = {
  blocks: DocBlock[];
  assets: DocAsset[];
  onAddBlock: (type: DocBlockType) => void;
  onMoveBlock: (blockId: string, direction: "up" | "down") => void;
  onRemoveBlock: (blockId: string) => void;
  onUpdateBlock: (block: DocBlock) => void;
};

export function DocumentBlockList(props: Props) {
  const { blocks, assets, onAddBlock, onMoveBlock, onRemoveBlock, onUpdateBlock } = props;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {DOC_BLOCK_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onAddBlock(type)}
            className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            + {DOC_BLOCK_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {blocks.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No blocks yet. Add a heading or paragraph to start the document.
        </p>
      ) : (
        <div className="space-y-2">
          {blocks.map((block, idx) => (
            <DocumentBlockRow
              key={block.id}
              block={block}
              assets={assets}
              canMoveUp={idx > 0}
              canMoveDown={idx < blocks.length - 1}
              canDelete={blocks.length > 1}
              onChange={onUpdateBlock}
              onMove={(dir) => onMoveBlock(block.id, dir)}
              onDelete={() => onRemoveBlock(block.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function appendBlock(blocks: DocBlock[], type: DocBlockType): DocBlock[] {
  return [...blocks, createDocBlock(type, blocks.length)];
}
